import Image from 'next/image';
import { Button } from '@components/Common';
import { useRouter } from 'next/router';
import { useTailwindBreakpoint } from '@components/Contexts/TailwindBreakpointContext';
import { FC, useEffect, useState } from 'react';
import Service from '../Service/Service';
import ServiceAnimation1 from './ServiceAnimation1/ServiceAnimation1';
import ServiceAnimation2 from './ServiceAnimation2/ServiceAnimation2';
import { useLocale } from '@components/Contexts/LocaleContext';
import { translate2 as translate } from 'utils/translate';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface Props {
  click: any;
}

const ServiceSection: FC<Props> = ({ click }) => {
  const { lang } = useLocale();
  const router = useRouter();
  const { screenWidth } = useTailwindBreakpoint();
  const [initWidth, setInitWidth] = useState(null);
  const { ref, inView } = useInView({ threshold: 0.5, triggerOnce: true });

  useEffect(() => {
    setInitWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    if (screenWidth) {
      setInitWidth(null);
    }
  }, [screenWidth]);
  return (
    <section
      id="service"
      className={initWidth >= 1024 || screenWidth >= 1024 ? 'Container' : ''}
    >
      <div className="px-4 md:px-12 lg:px-0 relative">
        <h1 className="relative text-h5MobileBold lg:text-h5Bold gradient-head-large text-center py-8">
          {lang === 'en' ? (
            <>Start securing your organization today with CODIUM e-Signature</>
          ) : (
            <>เริ่มปกป้ององค์กรของคุณวันนี้ ด้วย CODIUM e-Signature</>
          )}
        </h1>

        <div
          ref={ref}
          className="relative flex flex-col md:flex-row justify-center items-center pb-5"
        >
          <motion.div
            className="md:-ml-[5rem]"
            initial={{ opacity: 0, scale: 0 }}
            animate={
              inView
                ? {
                    opacity: 1,
                    scale: [0, 1],
                    transition: { type: 'spring', delay: 0.35, duration: 1 },
                  }
                : { opacity: 0, scale: 1 }
            }
          >
            <Image
              src="/images/e-signature/windows.webp"
              alt="สร้างเอกสารอนุมัติและลงนามอย่างปลอดภัยด้วย e-Signature"
              width={601}
              height={320}
            />
          </motion.div>
          <div className="text-white md:w-[50%]">
            <div className="ml-auto w-fit">
              <motion.h2
                className="pb-5 text-p2Semibold"
                initial={{ opacity: 0, x: 20 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
                viewport={{ amount: 0.5, once: true }}
                transition={{ duration: 1, delay: 0.3 }}
              >
                {translate(
                  'สร้างเอกสารอนุมัติและลงนามอย่างปลอดภัยด้วย e-Signature',
                  'Create approval documents and securely sign them with your electronic signature using our e-Signature system.',
                  lang,
                )}
              </motion.h2>
              <motion.p
                className="text-p3Regular"
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : { opacity: 0 }}
                viewport={{ amount: 0.5, once: true }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                {lang === 'en' ? (
                  <>
                    Empower your document workflow securely with{' '}
                    <span className="text-p3Semibold text-lightBlue">
                      e-Signature.
                    </span>{' '}
                    Generate approval requests and sign documents effortlessly
                    with your electronic signature. Streamline approvals, ensure
                    legal compliance, and eliminate paper-based processes for a
                    more efficient and secure organization.
                  </>
                ) : (
                  <>
                    ทำให้กระบวนการอนุมัติและลงนามเป็นเรื่องง่ายและปลอดภัย ด้วย{' '}
                    <span className="text-p3Semibold text-lightBlue">
                      e-Signature
                    </span>{' '}
                    คุณสามารถสร้างคำขออนุมัติและลงนามในเอกสารได้อย่างรวดเร็ว
                    ลดขั้นตอนที่ยุ่งยาก และมั่นใจได้ว่าทุกลายเซ็นมีผลทางกฎหมาย
                    นอกจากนี้ยังช่วยลดการใช้เอกสารกระดาษ
                    เพิ่มความคล่องตัวให้กับองค์กร
                    และทำให้การดำเนินงานเป็นระบบดิจิทัลที่มีประสิทธิภาพและปลอดภัยมากยิ่งขึ้น
                  </>
                )}
              </motion.p>
              <motion.div
                className="mt-5 w-fit mx-auto lg:mx-0"
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : { opacity: 0 }}
                viewport={{ amount: 0.5, once: true }}
                transition={{ duration: 1, delay: 0.6 }}
              >
                <Button
                  id="e-memo"
                  icon="/icons/speech.svg"
                  text={translate('ติดต่อเรา', 'CONTACT US', lang)}
                  className="text-sm border border-white"
                  onClick={click}
                />
              </motion.div>
            </div>
          </div>
        </div>

        <h1 className="relative text-h5MobileBold lg:text-h5Bold gradient-head-large text-center py-8">
          {lang === 'en' ? (
            <>We offer an end-to-end e-Signature management platform.</>
          ) : (
            <>
              ต้องการแค่ Digital Signing? <br className="md:hidden" />{' '}
              เรามีบริการ “e-Digital Signature”
            </>
          )}
        </h1>

        <div className="relative flex flex-col md:flex-row justify-center pb-5">
          <Service
            h2={translate(
              'บริการ Digital Certificate Management',
              'Digital Certificate Management Service',
              lang,
            )}
            paragraph={
              lang === 'en' ? (
                <>
                  Our professional dashboard simplifies Digital Certificate
                  management—easily create, delete, and revoke certificates
                  while seamlessly executing digital signatures.
                </>
              ) : (
                <>
                  <span className="text-p3Semibold">Dashboard</span>{' '}
                  สำหรับจัดการใบรับรองอิเล็กทรอนิกส์ (Digital Certificate)
                  <br className="hidden lg:block" />
                  มีชุดคำสั่่งสำหรับสร้าง, ลบ และถอนการใช้งานใบรับรอง
                  <br className="hidden lg:block" />
                  สามารถเรียกใช้ใบรับรองในการลงลายมือชื่อดิจิทัล (Digital
                  Signature) ได้
                </>
              )
            }
            animation={<ServiceAnimation1 />}
          />

          <Service
            h2={translate(
              'บริการ Digital Signing API Integration',
              'Digital Signing API Integration Service',
              lang,
            )}
            paragraph={
              lang === 'en' ? (
                <>
                  For organizations with existing document management systems,
                  we offer an API solution or backend integration for seamless
                  digital signing. Our service enables document signing without
                  disrupting your current workflow.
                </>
              ) : (
                <>
                  สำหรับองค์กรที่มี
                  <span className="text-p3Semibold">
                    ระบบจัดการเอกสารอยู่แล้ว
                    และต้องการลงนามด้วยลายมือชื่อดิจิทัล
                  </span>{' '}
                  (Digital Signature) เรามีบริการ{' '}
                  <span className="text-p3Semibold">API Solution</span>
                  <br className="lg:hidden" /> หรือ
                  <span className="text-p3Semibold">ระบบหลังบ้าน</span>
                  ที่สามารถเชื่อมต่อกับระบบเดิมที่มีอยู่
                  และนำเอกสารที่ออกจากระบบมาทำการลงลายมือชื่ออิเล็กทรอนิกส์ได้{' '}
                  <br />
                  ช่วยให้องค์กรลงนามบนเอกสารได้โดย
                  <span className="text-p3Semibold">
                    ไม่ต้องเปลี่ยนจากการทำงานแบบเดิม
                  </span>
                </>
              )
            }
            animation={<ServiceAnimation2 />}
          />
        </div>

        <div className="relative pb-10 w-fit mx-auto">
          <Button
            id="contact-us-esignature-service"
            icon="/icons/speech.svg"
            text={translate('ติดต่อเรา', 'CONTACT US', lang)}
            className="text-sm border border-white"
            onClick={click}
          />
        </div>
      </div>
    </section>
  );
};

export default ServiceSection;
